// The New A/B test wizard (spec/dashboard.md section 8.4; spec/liveops-engine.md section 11.2): one dialog in the offer
// wizard's style that stages a whole test — What (the name, the document and the parameters to vary), Who (Everyone or a
// segment as the test's condition, the target share and the users type), Variants (Control and Variant A by default, a
// weight each, the value matrix: Control keeps the base value) and Goal (the primary metric with its baseline, the minimum
// detectable uplift, α and power, and the required sample per variant with the days it takes at the environment's pace).
// Create (staged) stages the test (Inactive), its variants, its condition and its variant override through
// createAbTestFor, all linked by temp ids, so one Save All commits them together.
import { useEffect, useMemo, useState } from 'react';
import type { Document, Json, Parameter } from '../../api/types';
import { useContent } from '../../content/ContentContext';
import { CONDITION_NS } from '../../content/conditions/model';
import { formatValue } from '../../content/format';
import { useEntityFigures, n, type EntityKind } from '../../content/liveops/analytics';
import { useLiveOpsEnv } from '../../content/liveops/env';
import { AB_METRICS, AB_DEFAULTS, countText, metricOf, minorOf, sampleSizeMean, sampleSizeProportion, useAnalyticsDaily, type AbMetricKey } from '../../content/liveops/stats';
import { createAbTestFor, stageSegmentCondition, type VariantSpec } from '../../content/overrides/create';
import { AB_TEST, resolveDocument, valueAt, type OverridableLeaf } from '../../content/overrides/model';
import { ParameterPickDialog } from '../../content/overrides/ParameterPickDialog';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { ReferencePicker } from '../../content/widgets/ReferenceField';
import { heirsOf, useWidgetEnv } from '../../content/widgets/context';
import { Button, IconButton } from '../../ui/Button';
import { Chip } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { Plus, X } from '../../ui/icons';
import { NumberInput, Select, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { Tabs } from '../../ui/Tabs';
import { useToasts } from '../../ui/Toast';
import { gameToday } from '../../workspace/analyticsRange';
import { EVENT, OFFER } from './cards';
import { SEGMENT_OPTION } from './OfferWizard';
import './liveops.css';

const DAY = 86400;

/** The entity kind of the analytics read whose conversion is a document's baseline (offers and events), or null. */
function kindOf(catalog: ReturnType<typeof useContent>, template: string): EntityKind | null {
  const inFamily = (root: string) => template === root || heirsOf(catalog, root).some((t) => t.name === template);
  if (inFamily(OFFER)) return 'offer';
  if (inFamily(EVENT)) return 'event';
  return null;
}

/** `YYYY-MM-DD` shifted by whole days. */
function shiftDay(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY * 1000).toISOString().slice(0, 10);
}

/** The variant names after Control: Variant A, Variant B… */
function variantName(index: number): string {
  return index === 0 ? 'Control' : `Variant ${String.fromCharCode('A'.charCodeAt(0) + index - 1)}`;
}

/** Equal weights summing to 100 (the remainder to the first ones). */
function equalWeights(count: number): number[] {
  const base = Math.floor(100 / count);
  return Array.from({ length: count }, (_, i) => base + (i < 100 - base * count ? 1 : 0));
}

function Head({ children }: { children: string }) {
  return (
    <div className="section-head">
      <h3>{children}</h3>
    </div>
  );
}

export interface AbTestWizardProps {
  onClose: () => void;
  /** after the test is staged (the page opens its card) */
  onCreated?: (testId: string) => void;
  /** the document to vary, preselected (a document's "Create A/B test…"): its parameter dialog opens at once */
  document?: Document | null;
}

export function AbTestWizard({ onClose, onCreated, document: initial = null }: AbTestWizardProps) {
  const { workspace } = useWidgetEnv();
  const catalog = useContent();
  const toasts = useToasts();
  const { env } = useLiveOpsEnv();
  const testDef = catalog.templates.get(AB_TEST);
  const testParams = testDef?.effectiveParameters ?? [];
  const usersTypeParam = testParams.find((p) => p.name === 'usersType');
  const anyRef = testParams.find((p) => p.type === 'document');
  const hasSegments = catalog.templates.has(SEGMENT_OPTION) && catalog.templates.has(`${CONDITION_NS}SegmentCondition`);

  // ---- What
  const [name, setName] = useState('');
  const [target, setTarget] = useState<Document | null>(initial);
  const [leaves, setLeaves] = useState<OverridableLeaf[]>([]);
  const [picking, setPicking] = useState<'document' | 'params' | null>(null);
  // a preselected document opens its parameter dialog once the wizard is up (so the dialog is the topmost modal)
  useEffect(() => {
    if (initial) setPicking('params');
  }, [initial]);
  // ---- Who
  const [who, setWho] = useState<'everyone' | 'segment'>('everyone');
  const [segmentId, setSegmentId] = useState<string | null>(null);
  const [targetUsers, setTargetUsers] = useState(100);
  const [usersType, setUsersType] = useState<number>(2);
  // ---- Variants
  const [variants, setVariants] = useState<VariantSpec[]>(() => equalWeights(2).map((weight, i) => ({ name: variantName(i), weight, values: {} })));
  const [weightsTouched, setWeightsTouched] = useState(false);
  // ---- Goal
  const [metricKey, setMetricKey] = useState<AbMetricKey>('conversion');
  const metric = metricOf(metricKey);
  const [baselineTyped, setBaselineTyped] = useState<number | null>(null);
  const [sd, setSd] = useState<number | null>(null);
  const [upliftPct, setUpliftPct] = useState(AB_DEFAULTS.uplift * 100);
  const [alpha, setAlpha] = useState<number>(AB_DEFAULTS.alpha);
  const [power, setPower] = useState<number>(1 - AB_DEFAULTS.betta);

  // the environment's last 30 days: the daily new / active users and the revenue per active user
  const today = gameToday(env);
  const daily = useAnalyticsDaily(env, shiftDay(today, -29), today, false);
  const days = daily.data?.days ?? [];
  const newPerDay = days.length ? days.reduce((s, d) => s + n(d.newUsers), 0) / days.length : null;
  const activePerDay = days.length ? days.reduce((s, d) => s + n(d.uniqueUsers), 0) / days.length : null;
  const revenue = daily.data?.totals.revenue?.[0];
  const unique = n(daily.data?.totals.uniqueUsers);
  const arpuSuggestion = revenue && unique > 0 ? Number(revenue.amount) / minorOf(revenue.currency) / unique : null;
  // the document's conversion over the last 30 days (buyers / users of an offer or an event)
  const kind = target ? kindOf(catalog, target.template) : null;
  const figures = useEntityFigures(kind ?? 'offer', kind && target ? [target.id] : []);
  const entity = target ? figures.get(target.id) : undefined;
  const conversionSuggestion = entity && n(entity.users.month) > 0 ? (n(entity.buyers.month) / n(entity.users.month)) * 100 : null;
  const suggestion = metric.kind === 'proportion' ? conversionSuggestion : arpuSuggestion;
  const baseline = baselineTyped ?? suggestion;
  useEffect(() => setBaselineTyped(null), [metricKey]);

  // the required sample per variant and the days it takes
  const uplift = upliftPct / 100;
  const betta = 1 - power;
  const required = useMemo(() => {
    if (baseline === null) return null;
    return metric.kind === 'proportion' ? sampleSizeProportion(baseline / 100, uplift, alpha, betta) : sampleSizeMean(baseline, sd ?? 0, uplift, alpha, betta);
  }, [baseline, metric.kind, uplift, alpha, betta, sd]);
  const pace = usersType === 1 ? activePerDay : newPerDay;
  const paceWord = usersType === 1 ? 'active users' : 'new users';
  const eligiblePerDay = pace !== null ? (pace * targetUsers) / 100 : null;
  const daysNeeded = required !== null && eligiblePerDay && eligiblePerDay > 0 ? Math.ceil((required * variants.length) / eligiblePerDay) : null;

  const setWeight = (i: number, weight: number) => {
    setWeightsTouched(true);
    setVariants((vs) => vs.map((v, j) => (j === i ? { ...v, weight } : v)));
  };
  const addVariant = () =>
    setVariants((vs) => {
      const weights = weightsTouched ? null : equalWeights(vs.length + 1);
      const next = [...vs, { name: variantName(vs.length), weight: weightsTouched ? (vs.at(-1)?.weight ?? 50) : 0, values: {} }];
      return weights ? next.map((v, i) => ({ ...v, weight: weights[i]! })) : next;
    });
  const removeVariant = (i: number) =>
    setVariants((vs) => {
      const next = vs.filter((_, j) => j !== i);
      const weights = weightsTouched ? null : equalWeights(next.length);
      return weights ? next.map((v, j) => ({ ...v, weight: weights[j]! })) : next;
    });
  const setValue = (i: number, path: string, value: Json) => setVariants((vs) => vs.map((v, j) => (j === i ? { ...v, values: { ...v.values, [path]: value } } : v)));
  const baseOf = (leaf: OverridableLeaf): Json => (target ? ((valueAt(target.data as Json, leaf.path) ?? null) as Json) : null);
  const valueOf = (i: number, leaf: OverridableLeaf): Json => {
    const v = variants[i]?.values?.[leaf.path];
    return v === undefined ? baseOf(leaf) : v;
  };

  const segmentParam: Parameter | null = anyRef ? { ...anyRef, name: 'segment', displayName: 'Segment', type: 'document', template: SEGMENT_OPTION, of: null, description: null } : null;
  const audienceOk = who === 'everyone' || (typeof segmentId === 'string' && segmentId.length > 0);
  const canCreate = !!testDef && name.trim().length > 0 && audienceOk && variants.length >= 2 && variants.every((v) => v.weight > 0) && targetUsers > 0 && targetUsers <= 100;

  const create = () => {
    try {
      const env2 = { workspace, catalog };
      const condition = who === 'segment' && segmentId ? stageSegmentCondition(env2, segmentId) : null;
      const specs: VariantSpec[] = variants.map((v, i) => ({ name: v.name.trim() || variantName(i), weight: v.weight, values: Object.fromEntries(leaves.map((l) => [l.path, i === 0 ? baseOf(l) : valueOf(i, l)])) }));
      const testId = createAbTestFor(env2, target, target ? leaves : [], {
        name: name.trim(),
        variants: specs,
        condition,
        targetUsers,
        usersType,
        metric: metric.key,
        uplift,
        alpha,
        betta: Math.round(betta * 1e6) / 1e6,
        ...(required !== null ? { totalUsers: required * variants.length } : {}),
        ...(eligiblePerDay !== null && eligiblePerDay > 0 ? { usersPerDay: Math.round(eligiblePerDay * 100) / 100 } : {}),
      });
      toasts.success('A/B test staged (Inactive); Save All commits it, Run test starts it.');
      onClose();
      onCreated?.(testId);
    } catch (e) {
      toasts.error(e instanceof Error ? e.message : String(e));
    }
  };

  const metricUnit = metric.kind === 'proportion' ? '%' : revenue?.currency ?? 'per user';
  return (
    <Modal
      title="New A/B test"
      open
      onClose={onClose}
      size="l"
      className="offer-wizard ab-wizard"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={create} disabled={!canCreate}>
            Create (staged)
          </Button>
        </>
      }
    >
      {!testDef ? (
        <p className="muted">No ABTest template on this branch.</p>
      ) : (
        <div className="lo-wizard" data-testid="ab-wizard">
          <Head>What</Head>
          <div className="form-grid">
            <Field label="Name" required help="For the team; the players never see it">
              <TextInput value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" autoFocus />
            </Field>
            <Field label="Document to vary" help="Optional: a test without overrides only splits the players (the game reads the variant)">
              <span className="lo-abw-doc">
                {target ? <strong data-testid="ab-wizard-doc">{catalog.label(target)}</strong> : <span className="muted">None</span>}
                <Button size="s" onClick={() => setPicking('document')}>
                  {target ? 'Change…' : 'Pick document…'}
                </Button>
                {target && (
                  <Button size="s" variant="ghost" onClick={() => setPicking('params')}>
                    Parameters…
                  </Button>
                )}
              </span>
            </Field>
          </div>
          {target && (
            <div className="lo-abw-chips" aria-label="Parameters to vary">
              {leaves.length === 0 ? (
                <span className="lo-note">No parameter picked: Parameters… chooses what the variants change.</span>
              ) : (
                leaves.map((l) => (
                  <Chip key={l.path} onRemove={() => setLeaves((ls) => ls.filter((x) => x.path !== l.path))} removeLabel={`Remove ${l.label}`}>
                    {l.label}
                  </Chip>
                ))
              )}
            </div>
          )}

          <Head>Who</Head>
          {hasSegments && <Tabs segmented ariaLabel="Who joins" value={who} onChange={(k) => setWho(k as 'everyone' | 'segment')} items={[{ key: 'everyone', label: 'Everyone' }, { key: 'segment', label: 'In a segment' }]} />}
          <div className="form-grid">
            {who === 'segment' && segmentParam && (
              <Field label="Segment" required help="The test's condition: only players in this segment join" className="span-2">
                <FieldWidget param={segmentParam} value={segmentId} onCommit={(v) => setSegmentId(typeof v === 'string' ? v : null)} />
              </Field>
            )}
            <Field label="Target users" help="The percentage of the eligible players who join (%)">
              <NumberInput kind="int" value={targetUsers} onCommit={(v) => setTargetUsers(Math.min(100, Math.max(1, Number(v ?? 100))))} aria-label="Target users" />
            </Field>
            <Field label="Users type" help="New: only players who start after the test does; Old: only existing players; All: both">
              {usersTypeParam ? (
                <FieldWidget param={{ ...usersTypeParam, displayName: 'Users type' }} value={usersType} onCommit={(v) => setUsersType(Number(v ?? 2))} />
              ) : (
                <Select value={String(usersType)} onChange={(e) => setUsersType(Number(e.target.value))} aria-label="Users type">
                  <option value="0">New</option>
                  <option value="1">Old</option>
                  <option value="2">All</option>
                </Select>
              )}
            </Field>
          </div>

          <Head>Variants</Head>
          <div className="lo-abw-matrix" role="table" aria-label="Variants">
            <div className="lo-abw-row lo-abw-head" role="row" style={{ gridTemplateColumns: `minmax(140px, 1.2fr) repeat(${variants.length}, minmax(120px, 1fr))` }}>
              <span role="columnheader">Variant</span>
              {variants.map((v, i) => (
                <span key={i} role="columnheader" className="lo-abw-variant">
                  <TextInput size="s" value={v.name} onChange={(e) => setVariants((vs) => vs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} aria-label={`Variant ${i + 1} name`} />
                  {i >= 2 && (
                    <IconButton label={`Remove ${v.name || variantName(i)}`} onClick={() => removeVariant(i)}>
                      <X aria-hidden="true" />
                    </IconButton>
                  )}
                </span>
              ))}
            </div>
            <div className="lo-abw-row" role="row" style={{ gridTemplateColumns: `minmax(140px, 1.2fr) repeat(${variants.length}, minmax(120px, 1fr))` }}>
              <span role="rowheader">Weight</span>
              {variants.map((v, i) => (
                <span key={i} role="cell">
                  <NumberInput size="s" kind="int" value={v.weight} onCommit={(w) => setWeight(i, Math.max(0, Number(w ?? 0)))} aria-label={`${v.name || variantName(i)} weight`} />
                </span>
              ))}
            </div>
            {leaves.map((l) => (
              <div key={l.path} className="lo-abw-row" role="row" style={{ gridTemplateColumns: `minmax(140px, 1.2fr) repeat(${variants.length}, minmax(120px, 1fr))` }}>
                <span role="rowheader" className="lo-ab-field">
                  {l.label}
                </span>
                {variants.map((v, i) => (
                  <span key={i} role="cell">
                    {i === 0 ? (
                      <span className="lo-ab-base" title="Control keeps the base value" data-testid={`ab-wizard-base-${l.path}`}>
                        {formatValue(l.param, baseOf(l), catalog.format)}
                      </span>
                    ) : (
                      <FieldWidget param={{ ...l.param, name: 'value', displayName: `${l.label} for ${v.name || variantName(i)}` }} value={valueOf(i, l)} compact onCommit={(val) => setValue(i, l.path, val)} />
                    )}
                  </span>
                ))}
              </div>
            ))}
          </div>
          <div className="lo-abw-foot">
            <Button size="s" variant="ghost" onClick={addVariant} icon={<Plus aria-hidden="true" />}>
              Add variant
            </Button>
            <span className="lo-note">Weights are relative: {variants.map((v) => `${v.name || '…'} ${(variants.reduce((s, x) => s + x.weight, 0) > 0 ? (v.weight / variants.reduce((s, x) => s + x.weight, 0)) * 100 : 0).toFixed(0)} %`).join(' · ')}</span>
          </div>

          <Head>Goal</Head>
          <div className="form-grid">
            <Field label="Primary metric" help={metric.help}>
              <Select value={metric.key} onChange={(e) => setMetricKey(e.target.value as AbMetricKey)} aria-label="Primary metric">
                {AB_METRICS.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={`Baseline (${metricUnit})`}
              help={
                suggestion !== null && baselineTyped === null
                  ? metric.kind === 'proportion'
                    ? `The document's conversion in ${env?.slug ?? 'the environment'}, last 30 days`
                    : `Revenue per active user in ${env?.slug ?? 'the environment'}, last 30 days`
                  : metric.kind === 'proportion'
                    ? 'The control’s expected conversion; no figure for this document yet, so type one'
                    : 'The control’s expected value per user; type one'
              }
            >
              <NumberInput kind="float" allowEmpty value={baseline === null ? '' : Number(baseline.toFixed(4))} onCommit={(v) => setBaselineTyped(v === null ? null : Number(v))} aria-label="Baseline" />
            </Field>
            {metric.kind === 'mean' && (
              <Field label="Standard deviation" help="Of the metric per user, in the baseline's unit (revenue is skewed: often 3–10× the mean)">
                <NumberInput kind="float" allowEmpty value={sd ?? ''} onCommit={(v) => setSd(v === null ? null : Number(v))} aria-label="Standard deviation" />
              </Field>
            )}
            <Field label="Minimum detectable uplift (%)" help="The smallest relative improvement worth detecting">
              <NumberInput kind="float" value={upliftPct} onCommit={(v) => setUpliftPct(Number(v ?? 10))} aria-label="Minimum detectable uplift" />
            </Field>
            <Field label="Significance (α)" help="The false-positive rate of the two-sided test">
              <NumberInput kind="float" value={alpha} onCommit={(v) => setAlpha(Math.min(0.5, Math.max(0.0001, Number(v ?? 0.05))))} aria-label="Significance" />
            </Field>
            <Field label="Power (1 − β)" help="The chance to detect the uplift when it is real">
              <NumberInput kind="float" value={power} onCommit={(v) => setPower(Math.min(0.9999, Math.max(0.5, Number(v ?? 0.8))))} aria-label="Power" />
            </Field>
          </div>
          <p className="lo-abw-sample" data-testid="ab-wizard-sample" aria-live="polite">
            {required !== null ? (
              <>
                Required sample: <strong>~{countText(required)}</strong> players per variant ({countText(required * variants.length)} in all)
              </>
            ) : baseline === null ? (
              'Required sample: not estimable without a baseline.'
            ) : metric.kind === 'mean' && !(sd && sd > 0) ? (
              'Required sample: not estimable without a standard deviation.'
            ) : (
              'Required sample: not estimable from these figures.'
            )}
          </p>
          {required !== null && (
            <p className="lo-note" data-testid="ab-wizard-days">
              {daysNeeded !== null && pace !== null
                ? `At ${countText(pace)} ${paceWord} a day in ${env?.slug ?? 'the environment'} (${targetUsers} % join): ~${countText(daysNeeded)} day${daysNeeded === 1 ? '' : 's'}.`
                : `The environment's daily ${paceWord} are not known yet, so no duration.`}
            </p>
          )}
        </div>
      )}
      {picking === 'document' && (
        <ReferencePicker
          param={{ template: null, name: 'document', displayName: 'Document to vary', type: 'document' }}
          selected={target ? [target.id] : []}
          onClose={() => setPicking(null)}
          onPick={(ids) => {
            setPicking(null);
            if (ids[0])
              void resolveDocument(catalog, ids[0]).then((doc) => {
                if (!doc) return;
                setTarget(doc);
                setLeaves([]);
                setVariants((vs) => vs.map((v) => ({ ...v, values: {} })));
                setPicking('params');
              });
          }}
        />
      )}
      {picking === 'params' && target && (
        <ParameterPickDialog
          doc={target}
          title="Params to vary"
          confirmLabel="Use these parameters"
          onClose={() => setPicking(null)}
          onPick={(picked) => {
            setLeaves(picked);
            setPicking(null);
          }}
        />
      )}
    </Modal>
  );
}
